'use client'

import { Option } from '@mui/base'
import { clsx } from 'clsx'
import { type CSSProperties, type FC, useMemo } from 'react'
import {
  useRefinementList,
  type UseRefinementListProps,
} from 'react-instantsearch'
import { useIntl } from 'react-intl'

import { IconCaretRight } from '@redteclab/icons'

import { productSearchGetRefinementLabel } from '../productSearchGetRefinementLabel'
import {
  ProductSearchRefinement,
  ProductSearchRefinementContext,
} from '../ProductSearchRefinement'
import { useProductSearchRefinementStateContext } from '../ProductSearchRefinementStateContext'

import { ProductSearchRefinementCategoryListbox } from './ProductSearchRefinementCategoryListbox'

import styles from './ProductSearchRefinementCategoryList.module.css'

/**
 * Top level category example, where `value` has two parts divided by `/`:
 * @example
 * ```json
 * {
 *     "count": 476,
 *     "isRefined": false,
 *     "value": "shop-apotheke.com/Baby & Familie",
 *     "label": "shop-apotheke.com/Baby & Familie",
 *     "highlighted": "shop-apotheke.com/Baby & Familie"
 * }
 * ```
 */
const TOP_LEVEL_DEPTH = 2

type Item = ReturnType<typeof useRefinementList>['items'][number]

const useOptionItems = (items: Item[], refinedValues: string[]): Item[] => {
  const intl = useIntl()

  const labelCollator = useMemo(
    () => new Intl.Collator(intl.locale),
    [intl.locale],
  )

  return useMemo(
    () =>
      items
        .toSorted((itemA, itemB) =>
          labelCollator.compare(itemA.label, itemB.label),
        )
        .filter((item) => {
          const itemValueParts = item.value.split('/')

          if (itemValueParts.length === 1) {
            // exclude top level category
            return false
          }

          if (refinedValues.length === 0) {
            // show only top level categories if no refinement is selected
            return itemValueParts.length === TOP_LEVEL_DEPTH
          }

          return refinedValues.some((refinedValue) => {
            const refinedValueParts = refinedValue.split('/')

            return (
              // keep current and ancestors categories
              refinedValue.startsWith(item.value) ||
              // keep children (not all descendants) categories
              (item.value.startsWith(refinedValue) &&
                itemValueParts.length <= refinedValueParts.length + 1)
            )
          })
        }),
    [items, labelCollator, refinedValues],
  )
}

const showCount = (item: Item, refinedValues: string[]): boolean =>
  // display counter for top level categories when no refinements are selected
  refinedValues.length === 0 ||
  refinedValues.some(
    // display counter for children categories when a parent category is selected
    (value) => item.value.startsWith(value) && item.value !== value,
  )

const PRODUCT_SEARCH_REFINEMENT_CATEGORY_LIST_FACET_LIMIT = 10_000

type ProductSearchRefinementCategoryListProps = Pick<
  UseRefinementListProps,
  'attribute'
> & {
  hidden: boolean
}

export const ProductSearchRefinementCategoryList: FC<
  ProductSearchRefinementCategoryListProps
> = ({ attribute, hidden }) => {
  const intl = useIntl()

  const {
    state: { isMobile },
  } = useProductSearchRefinementStateContext()

  const refinementList = useRefinementList({
    attribute,
    limit: PRODUCT_SEARCH_REFINEMENT_CATEGORY_LIST_FACET_LIMIT,
  })

  const formLabel = productSearchGetRefinementLabel(attribute, intl)

  const { items, refine } = refinementList

  const refinedValues = useMemo(
    () => items.filter((item) => item.isRefined).map((item) => item.value),
    [items],
  )

  const optionItems = useOptionItems(items, refinedValues)

  const contextValue = useMemo(
    () => ({
      refinementList,
      refinementListOptions: {
        attribute,
        limit: PRODUCT_SEARCH_REFINEMENT_CATEGORY_LIST_FACET_LIMIT,
      },
    }),
    [attribute, refinementList],
  )

  if (hidden || items.length === 0) {
    return null
  }

  return (
    <ProductSearchRefinementContext value={contextValue}>
      <ProductSearchRefinement
        aria-label={formLabel}
        attribute={attribute}
        data-qa-id={`form-${formLabel}`}
        listboxOpen={isMobile ? true : undefined}
        multiple
        onChange={(event, values) => {
          const value = values.at(-1) ?? refinedValues.at(-1)

          if (value) {
            refine(value)
          }
        }}
        renderValue={() => (
          <>
            <span className="truncate">{formLabel}</span>
            {refinedValues.length > 0 && (
              <span className="hidden text-dark-primary-strong desktop:inline">
                {refinedValues.length}
              </span>
            )}
          </>
        )}
        slotProps={{
          popup: {
            keepMounted: isMobile,
          },
        }}
        slots={{
          listbox: ProductSearchRefinementCategoryListbox,
        }}
        value={refinedValues}
      >
        {optionItems.map((item) => {
          const labelParts = item.label.split('/')
          const label = labelParts.at(-1)?.trim()

          if (!label) {
            return null
          }

          return (
            <Option
              className={styles['product-search-category-list__option']}
              data-qa-id={`form-category-list-${label}`}
              key={item.value}
              // eslint-disable-next-line react/forbid-component-props -- needed for dynamic styling
              style={
                {
                  '--product-search-category-list-option-indent': `calc(${labelParts.length - TOP_LEVEL_DEPTH} * var(--spacing-3))`,
                } as CSSProperties
              }
              value={item.value}
            >
              <IconCaretRight
                className={clsx(
                  'size-6 shrink-0 text-dark-brand',
                  item.isRefined && 'rotate-90',
                )}
              />
              <span
                className={clsx(
                  'pt-0.5 desktop:pt-0 desktop:text-m',
                  item.isRefined && 'font-medium',
                )}
              >
                {label}
                {showCount(item, refinedValues) && ` (${item.count})`}
              </span>
            </Option>
          )
        })}
      </ProductSearchRefinement>
    </ProductSearchRefinementContext>
  )
}
